import { useNavigate } from 'react-router-dom'
import {
  UserCircle,
  Clock,
  UserPlus,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  PartyPopper,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useOnboardingStore } from '../stores/onboardingStore'

interface StepConfig {
  key: 'profileCompleted' | 'scheduleCompleted' | 'firstPatientCreated' | 'firstAppointmentCreated' | 'remindersConfigured'
  label: string
  description: string
  icon: React.ElementType
  actionLabel: string
  actionPath: string
  accent: string
}

const STEPS: StepConfig[] = [
  {
    key: 'profileCompleted',
    label: 'Completa tu perfil',
    description: 'Añade tu foto y especialidad',
    icon: UserCircle,
    actionLabel: 'Editar perfil',
    actionPath: '/profile',
    accent: 'bg-blue-50 text-blue-600 border-blue-200',
  },
  {
    key: 'scheduleCompleted',
    label: 'Configura tu horario',
    description: 'Define tus horas de trabajo',
    icon: Clock,
    actionLabel: 'Configurar',
    actionPath: '/settings',
    accent: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  },
  {
    key: 'firstPatientCreated',
    label: 'Crea tu primer paciente',
    description: 'Registra una ficha clínica',
    icon: UserPlus,
    actionLabel: 'Nuevo paciente',
    actionPath: '/patients',
    accent: 'bg-violet-50 text-violet-600 border-violet-200',
  },
  {
    key: 'firstAppointmentCreated',
    label: 'Añade tu primera cita',
    description: 'Programa una consulta',
    icon: CalendarPlus,
    actionLabel: 'Nueva cita',
    actionPath: '/appointments',
    accent: 'bg-amber-50 text-amber-600 border-amber-200',
  },
  // Ocultado: WhatsApp no está implementado en fase 1
  // {
  //   key: 'remindersConfigured',
  //   label: 'Conecta WhatsApp',
  //   description: 'Activa recordatorios automáticos',
  //   icon: MessageSquare,
  //   actionLabel: 'Configurar',
  //   actionPath: '/reminders',
  //   accent: 'bg-rose-50 text-rose-600 border-rose-200',
  // },
]

export function QuickStartChecklist({ inline = false }: { inline?: boolean }) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { progress, completeStep, getCompletionPercent } = useOnboardingStore()

  const percent = getCompletionPercent()
  const isComplete = percent >= 100

  const handleAction = (step: StepConfig) => {
    // Marcar como completado inmediatamente para steps que requieren navegación
    // En una implementación real, se marcaría después de completar la acción
    // pero para simplificar, lo marcamos al clickar
    completeStep(step.key)
    navigate(step.actionPath)
  }

  const containerClasses = inline
    ? 'bg-white border border-slate-200 rounded-2xl p-6 shadow-sm'
    : 'max-w-2xl mx-auto'

  return (
    <div className={containerClasses}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-slate-900">
            {isComplete ? (
              <span className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-amber-500" />
                ¡Onboarding completado!
              </span>
            ) : (
              'Checklist de inicio'
            )}
          </h2>
          <span className="text-sm font-semibold text-slate-500">{percent}%</span>
        </div>

        {/* Progress bar */}
        <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-700 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>

        {!isComplete && (
          <p className="mt-2 text-sm text-slate-500">
            Completa {Math.ceil((100 - percent) / 20)} paso{Math.ceil((100 - percent) / 20) > 1 ? 's' : ''} más para desbloquear todo el potencial de SmartDental.
          </p>
        )}
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {STEPS.map((step) => {
          const isDone = progress?.[step.key] ?? false
          const Icon = step.icon

          return (
            <div
              key={step.key}
              className={`group flex items-center gap-4 rounded-xl border p-4 transition-all ${
                isDone
                  ? 'border-slate-100 bg-slate-50/50 opacity-70'
                  : 'border-slate-200 bg-white hover:border-primary-200 hover:shadow-sm'
              }`}
            >
              {/* Icon / Check */}
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${
                  isDone
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                    : step.accent
                }`}
              >
                {isDone ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${isDone ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                  {step.label}
                </p>
                <p className="text-xs text-slate-500">{step.description}</p>
              </div>

              {/* Action */}
              {!isDone && (
                <button
                  onClick={() => handleAction(step)}
                  className="flex items-center gap-1 rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                >
                  {step.actionLabel}
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Celebration */}
      {isComplete && (
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-600">
            ¡Enhorabuena{user?.firstName ? `, ${user.firstName}` : ''}! Tu clínica está lista para operar al 100%.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700"
          >
            Ir al Dashboard
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
